import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Image as ImageIcon, Pencil, Trash2, Check, X } from "lucide-react";
import { ImageUpload } from "@/components/ui/image-upload";

interface LogoDisplaySectionProps {
  logoUrl: string | undefined;
  onLogoChange: (url: string) => void;
}

const DEFAULT_HELP_GENIE_LOGO = "https://s3.ap-southeast-2.amazonaws.com/fs.enterprisedna.co/logos/helpgenie.png";

const LogoDisplaySection = ({ logoUrl, onLogoChange }: LogoDisplaySectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editUrl, setEditUrl] = useState(logoUrl || "");
  const [uploadMode, setUploadMode] = useState<"upload" | "url">("upload");

  const handleSave = () => {
    if (editUrl.trim()) {
      onLogoChange(editUrl);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditUrl(logoUrl || "");
    setIsEditing(false);
  };

  const handleRemove = () => {
    onLogoChange(DEFAULT_HELP_GENIE_LOGO);
  };

  const isDefaultLogo = logoUrl === DEFAULT_HELP_GENIE_LOGO || !logoUrl;

  if (isEditing) {
    return (
      <div className="space-y-3">
        <Label className="text-sm font-medium">Change Logo</Label>
        <Tabs value={uploadMode} onValueChange={(value) => setUploadMode(value as "upload" | "url")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">Upload Image</TabsTrigger>
            <TabsTrigger value="url">URL</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-3">
            <ImageUpload
              bucket="genie-images"
              value={logoUrl}
              onChange={onLogoChange}
              label="Logo Image"
              className="w-full"
            />
            <Button size="sm" variant="outline" onClick={handleCancel} className="w-full">
              Cancel
            </Button>
          </TabsContent>

          <TabsContent value="url" className="space-y-3">
            <div className="flex gap-2">
              <Input
                type="url"
                value={editUrl}
                onChange={(e) => setEditUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="flex-1 text-sm"
              />
              <Button size="sm" variant="ghost" onClick={handleSave}>
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                <X className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Enter a direct URL to your logo image (PNG, JPG, or SVG recommended)
            </p>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Logo</Label>
      <div className="flex items-center gap-4">
        {/* Logo preview */}
        <div className="relative group">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Email logo"
              className="w-20 h-20 rounded-lg object-contain bg-white border border-gray-200 shadow-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).src = DEFAULT_HELP_GENIE_LOGO;
              }}
            />
          ) : (
            <div className="w-20 h-20 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs"
            onClick={() => {
              setEditUrl(logoUrl || "");
              setIsEditing(true);
            }}
          >
            <Pencil className="h-3 w-3" />
            Change Logo
          </Button>

          {!isDefaultLogo && (
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5 text-xs text-gray-500 hover:text-red-600"
              onClick={handleRemove}
            >
              <Trash2 className="h-3 w-3" />
              Reset to Default
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoDisplaySection;
